/**
 * @file Renders the link that opens the change-email and confirmation
 * modals.
 */
import { useRef } from "react";
import { Auth } from "aws-amplify";
import { IdcardOutlined } from "@ant-design/icons";
import { Typography, Space } from "antd";

import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";

import ChangeEmailModal from "./ChangeEmailModal";
import ConfirmEmailModal from "./ConfirmEmailModal";
import type { ModalControls } from "../../Page/Modal/types";

const LABEL_CHANGE = "Change";

type CognitoSession = {
  signInUserSession: {
    accessToken: {
      jwtToken: string;
    };
  };
};

type IdentityAppContext = AppContext & {
  isImpersonated?: boolean;
};

type Props = {
  title?: string;
};

/**
 * Renders the link that opens the change-email modal, then chains
 * into the confirmation modal once a new email is submitted.
 */
const UpdateIdentityEmailLink = ({
  title = LABEL_CHANGE,
}: Props): JSX.Element => {
  const changeEmailModal = useRef<ModalControls>({});
  const confirmEmailModal = useRef<ModalControls>({});

  const { identity, isImpersonated } = useAppContext() as IdentityAppContext;

  /**
   * Opens the change-email modal with the current identity.
   */
  const onClick = async () =>
    changeEmailModal.current.openItem?.(identity as Record<string, unknown>);

  /**
   * Fetches the current access token and opens the confirmation modal
   * for the newly submitted email.
   */
  const onSuccess = async (email: string) => {
    const user = await Auth.currentAuthenticatedUser() as CognitoSession;

    const { signInUserSession } = user;
    const { jwtToken: accessToken } = signInUserSession.accessToken;

    confirmEmailModal.current.openItem?.({
      email,
      accessToken,
    });
  };

  const disabled = Boolean(isImpersonated);

  return (
    <>
      <Typography.Link
        strong
        onClick={onClick}
        disabled={disabled}
      >
        <Space>
          <IdcardOutlined />
          {title}
        </Space>
      </Typography.Link>

      <ChangeEmailModal
        modal={changeEmailModal}
        onSuccess={onSuccess}
      />

      <ConfirmEmailModal
        modal={confirmEmailModal}
      />
    </>
  );
};

export default UpdateIdentityEmailLink;
